/**
 * CourseArtwork — generate course preview images.
 * One generation, three cropped variants (1:1, 9:16, 16:9).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useCourseContext } from '../../CourseContext';
import { courseRunsService, getPresignedUrl } from '../../../../services/courseRunsService';
import { jobsService } from '../../../../services/jobsService';
import { IMAGE_MODEL_OPTIONS, imageModelLabel } from '../../../../constants/imageModels';

interface ArtworkData {
  prompt: string;
  model?: string;
  original_key: string;
  preview_key: string;
  preview_vertical_key: string;
  preview_horizontal_key: string;
}

const VARIANT_INFO = [
  { key: 'preview_key', label: 'Square (1:1)', usage: 'Course detail page, fallback', size: '600×600' },
  { key: 'preview_vertical_key', label: 'Vertical (9:16)', usage: 'Library listing cards', size: '400×600' },
  { key: 'preview_horizontal_key', label: 'Horizontal (16:9)', usage: 'Folder cards, lesson hero', size: '600×400' },
] as const;

function PresignedImage({ s3Key, alt, className }: { s3Key: string; alt: string; className?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const presigned = await getPresignedUrl(s3Key);
        if (!cancelled) setUrl(presigned);
      } catch {
        // silently fail — image just won't show
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [s3Key]);

  if (loading) return <div className="flex items-center justify-center py-8 text-xs text-gray-400">Loading...</div>;
  if (!url) return null;
  return <img src={url} alt={alt} className={className} />;
}

export function CourseArtwork() {
  const { run, document, refreshDocument } = useCourseContext();
  const courseRunId = run?.run_id;
  const artwork: ArtworkData | null = (document as any)?.artwork || null;

  const [prompt, setPrompt] = useState('');
  const [imageModel, setImageModel] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Seed prompt from artwork or auto-generate
  useEffect(() => {
    if (artwork?.prompt) {
      setPrompt(artwork.prompt);
    } else if (document) {
      const targeting = (document as any)?.targeting || {};
      const title = targeting.title || '';
      const desc = targeting.description || '';
      setPrompt(
        `A professional, modern course cover image for '${title}'. ` +
        `${desc.slice(0, 200)}${desc.length > 200 ? '...' : ''} ` +
        `Clean illustration style, muted professional tones with teal accents, ` +
        `centered subject with space around it for cropping, no text or lettering.`
      );
    }
  }, [artwork, document]);

  const handleGenerate = useCallback(async () => {
    if (!courseRunId) return;
    setGenerating(true);
    setError(null);
    try {
      const { job_id } = await courseRunsService.generateArtwork(courseRunId, prompt, imageModel || undefined);
      const finalJob = await jobsService.waitForCompletion(job_id, {});
      if (finalJob.status === 'failed') {
        throw new Error((finalJob as any).error || 'Artwork generation failed');
      }
      await refreshDocument();
      setRefreshKey((k) => k + 1);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  }, [courseRunId, prompt, imageModel, refreshDocument]);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-800">Course Artwork</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Generate a single image that will be cropped into three variants for the course and lesson headers.
        </p>
      </div>

      {/* Prompt editor */}
      <div>
        <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Image Prompt</label>
        <textarea
          className="w-full text-xs border rounded p-2 bg-gray-50 focus:bg-white focus:ring-1 focus:ring-blue-300 focus:border-blue-300 resize-y"
          rows={4}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe the course cover image..."
        />
      </div>

      {/* Image model */}
      <div>
        <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Image Model</label>
        <select
          className="text-xs border rounded p-2 bg-gray-50 focus:bg-white focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
          value={imageModel}
          onChange={(e) => setImageModel(e.target.value)}
        >
          <option value="">Default (Shared Resources → Image Generation Models)</option>
          {IMAGE_MODEL_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label} — {o.provider}</option>
          ))}
        </select>
        {artwork?.model && (
          <p className="text-[10px] text-gray-400 mt-1">Current artwork was generated with {imageModelLabel(artwork.model)}</p>
        )}
      </div>

      {/* Generate button */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium rounded-md text-white disabled:opacity-50"
          style={{ backgroundColor: '#1a2332' }}
          disabled={generating || !prompt.trim()}
          onClick={handleGenerate}
        >
          {generating ? 'Generating...' : artwork ? 'Regenerate Artwork' : 'Generate Artwork'}
        </button>
        {generating && (
          <span className="text-xs text-gray-500">This may take 30-60 seconds...</span>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">{error}</div>
      )}

      {/* Generated variants */}
      {artwork && (
        <div className="space-y-4">
          <div className="text-xs font-semibold text-gray-600 mt-2">Generated Variants</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {VARIANT_INFO.map(({ key, label, usage, size }) => {
              const s3Key = (artwork as any)[key];
              if (!s3Key) return null;
              return (
                <div key={key} className="border rounded overflow-hidden bg-white">
                  <div className="bg-gray-100 flex items-center justify-center p-2" style={{ minHeight: '160px' }}>
                    <PresignedImage
                      s3Key={s3Key}
                      alt={label}
                      className="max-w-full max-h-48 rounded"
                      key={`${s3Key}-${refreshKey}`}
                    />
                  </div>
                  <div className="p-2.5 space-y-0.5">
                    <div className="text-xs font-semibold text-gray-800">{label}</div>
                    <div className="text-[10px] text-gray-500">{size}</div>
                    <div className="text-[10px] text-gray-400">{usage}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!artwork && !generating && (
        <div className="text-xs text-gray-400 italic bg-gray-50 rounded p-4">
          No artwork generated yet. Edit the prompt above and click Generate.
        </div>
      )}
    </div>
  );
}
